<template>
  <!-- ChatwootWidget doesn't need a template as it's injected via script -->
</template>

<script>
export default {
  name: 'ChatwootWidget',
  props: {
    accountNumber: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      observer: null
    };
  },
  mounted() {
    // Delay initialization slightly to ensure proper loading
    setTimeout(() => {
      this.initializeChatwoot();
      
      // Watch for theme changes on the document element
      this.observer = new MutationObserver(this.handleThemeChange);
      this.observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme']
      });
    }, 500);
  },
  methods: {
    initializeChatwoot() {
      // First, clean up any existing widget
      this.cleanupWidget();
      
      // Check if there's already a Chatwoot script to avoid duplicates
      const existingScript = document.querySelector('script[src*="chat.invoicedatahub.com/packs/js/sdk.js"]');
      if (existingScript) {
        console.log('Chatwoot script already exists, skipping duplicate initialization');
        return;
      }
      
      // Get current theme to determine Chatwoot mode
      const currentTheme = document.documentElement.getAttribute('data-theme');
      const darkModeValue = currentTheme === 'dark' ? 'auto' : 'light';
      
      window.chatwootSettings = {
        position: "right",
        type: "expanded_bubble",
        launcherTitle: "Chat with us",
        darkMode: darkModeValue
      };

      const BASE_URL = "https://chat.invoicedatahub.com";
      const script = document.createElement('script');
      script.src = BASE_URL + "/packs/js/sdk.js";
      script.defer = true;
      script.async = true;
      script.id = 'chatwoot-sdk-script'; // Add ID for easier reference
      
      script.onload = () => {
        // Ensure the SDK is available before running
        if (window.chatwootSDK) {
          window.chatwootSDK.run({
            websiteToken: 'zeQYFwHWYA9Gkh5RYNdZeqXg',
            baseUrl: BASE_URL
          });
          
          // Set attributes when the widget is fully loaded
          this.setCustomAttributes();
        } else {
          console.error('Chatwoot SDK not available after script load');
        }
      };
      
      // Handle script load errors
      script.onerror = () => {
        console.error('Failed to load Chatwoot SDK script');
      };
      
      document.head.appendChild(script);
    },
    
    async setCustomAttributes() {
      try {
        // Fetch signed metadata from our API
        const response = await fetch('/api/chat/metadata', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to get signed chat metadata');
        }
        
        const signedMetadata = await response.json();
        
        // Set custom attributes when Chatwoot is ready
        const checkChatwoot = setInterval(() => {
          if (window.$chatwoot && window.$chatwoot.setCustomAttributes) {
            window.$chatwoot.setCustomAttributes({
              debtorcode: signedMetadata.debtorcode || "none",
              invoicenumber: signedMetadata.invoicenumber || "none",
              data_signature: signedMetadata.data_signature || "none"
            });
            clearInterval(checkChatwoot);
          }
        }, 500);
      } catch (error) {
        console.error('Error setting Chatwoot attributes:', error);
        
        // Fallback to using the prop directly if API call fails
        const checkChatwoot = setInterval(() => {
          if (window.$chatwoot && window.$chatwoot.setCustomAttributes) {
            window.$chatwoot.setCustomAttributes({
              debtorcode: this.accountNumber || "none",
              invoicenumber: "none"
            });
            clearInterval(checkChatwoot);
          }
        }, 500);
      }
    },
    
    /**
     * Public method that can be called to reload the widget
     * This is used when clearing cookies to restart the widget
     */
    reloadWidget() {
      // First clean up existing widget
      this.cleanupWidget();
      
      // Then reinitialize
      setTimeout(() => {
        this.initializeChatwoot();
      }, 500);
    },
    
    cleanupWidget() {
      try {
        if (window.$chatwoot) {
          // Hide widget before removal
          try {
            window.$chatwoot.toggleBubbleVisibility('hide');
          } catch (e) {
            console.error('Error hiding chatwoot bubble:', e);
          }
        }
        
        // Specifically target the main widget holder
        const widgetHolder = document.getElementById('cw-widget-holder');
        if (widgetHolder) {
          try {
            widgetHolder.parentNode.removeChild(widgetHolder);
          } catch (e) {
            console.error('Error removing cw-widget-holder:', e);
          }
        }
        
        // Remove any other Chatwoot DOM elements
        const chatwootWidgets = document.querySelectorAll('[class*="chatwoot"], [id*="chatwoot"], .woot--bubble-holder');
        chatwootWidgets.forEach(el => {
          try {
            if (el.parentNode) {
              el.parentNode.removeChild(el);
            }
          } catch (e) {
            console.error('Error removing chatwoot element:', e);
          }
        });
        
        // Remove script tag
        const chatwootScript = document.querySelector('script[src*="chat.invoicedatahub.com/packs/js/sdk.js"]');
        if (chatwootScript) {
          chatwootScript.remove();
        }
        
        // Clear cookies to ensure clean state
        document.cookie = '_chatwoot_session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        document.cookie = '_chatwoot_session=; Path=/; Domain=chat.invoicedatahub.com; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        document.cookie = 'cw_conversation=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        document.cookie = 'cw_conversation=; Path=/; Domain=chat.invoicedatahub.com; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        
        // Clean up global objects
        delete window.$chatwoot;
        delete window.chatwootSDK;
        delete window.chatwootSettings;
        
      } catch (error) {
        console.error('Error during chatwoot cleanup:', error);
      }
    },
    
    handleThemeChange(mutations) {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'data-theme') {
          // Get the new theme
          const newTheme = document.documentElement.getAttribute('data-theme');
          const darkModeValue = newTheme === 'dark' ? 'auto' : 'light';
          
          // Update Chatwoot theme if initialized
          if (window.$chatwoot && window.$chatwoot.setDarkMode) {
            window.$chatwoot.setDarkMode(darkModeValue);
          }
        }
      }
    }
  },
  
  beforeUnmount() {
    // Clean up Chatwoot when component is destroyed
    this.cleanupWidget();
    
    // Disconnect observer when component is destroyed
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
</script>